import { useState, useEffect } from 'react'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import {
  WIN_MESSAGES,
  GAME_COPIED_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  WORD_NOT_FOUND_MESSAGE,
  CORRECT_WORD_MESSAGE,
} from './constants/strings'
import {
  MAX_CHALLENGES,
  REVEAL_TIME_MS,
  GAME_LOST_INFO_DELAY,
  WELCOME_INFO_MODAL_MS,
} from './constants/settings'
import {
  isWordInWordList,
  isWinningWord,
  unicodeLength,
  getRandomWord,
  getDefinitionByWord,
} from './lib/words'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import {
  loadGameStateFromLocalStorage,
  saveGameStateToLocalStorage,
} from './lib/localStorage'
import { default as GraphemeSplitter } from 'grapheme-splitter'

import './App.css'
import { AlertContainer } from './components/alerts/AlertContainer'
import { useAlert } from './context/AlertContext'
import { Navbar } from './components/navbar/Navbar'
import { Instalador } from './components/navbar/Instalador'
import { Footer } from './components/navbar/Footer'

function App() {
  const { showError: showErrorAlert, showSuccess: showSuccessAlert, clearAlert } =
    useAlert()
  const [currentGuess, setCurrentGuess] = useState('')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [currentRowClass, setCurrentRowClass] = useState('')
  const [isGameLost, setIsGameLost] = useState(false)
  const [isRevealing, setIsRevealing] = useState(false)
  const [gameData, setGameData] = useState({
    solution: '',
    definition: {},
    wordLength: 5
  })
  const [guesses, setGuesses] = useState([])
  const [stats, setStats] = useState(() => loadStats())
  const [isInitialized, setIsInitialized] = useState(false)

  const canPlay = !isGameWon && !isGameLost && guesses.length < MAX_CHALLENGES
  const WORD_LENGTH = gameData.wordLength || unicodeLength(gameData.solution)

  useEffect(() => {
    const loaded = loadGameStateFromLocalStorage()
    if (loaded?.solution) {
      const gameWasWon = loaded.guesses.includes(loaded.solution)
      const gameWasLost = loaded.guesses.length >= MAX_CHALLENGES && !gameWasWon
      
      if (gameWasWon || gameWasLost) {
        const newWord = getRandomWord()
        setGameData({
          solution: newWord.solution,
          definition: newWord.definition,
          wordLength: unicodeLength(newWord.solution)
        })
        setGuesses([])
      } else {
        const savedDefinition = getDefinitionByWord(loaded.solution)
        
        setGameData({
          solution: loaded.solution,
          definition: savedDefinition,
          wordLength: unicodeLength(loaded.solution)
        })
        setGuesses(loaded.guesses)
        
        if (gameWasWon) {
          setIsGameWon(true)
        }
        if (gameWasLost) {
          setIsGameLost(true)
        }
      }
    } else {
      const newWord = getRandomWord()
      setGameData({
        solution: newWord.solution,
        definition: newWord.definition,
        wordLength: unicodeLength(newWord.solution)
      })
    }
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (!isInitialized) return
    
    if (!loadGameStateFromLocalStorage()) {
      setTimeout(() => {
        setIsInfoModalOpen(true)
      }, WELCOME_INFO_MODAL_MS)
    }
  }, [isInitialized])

  useEffect(() => {
    if (!isInitialized || !gameData.solution) return
    saveGameStateToLocalStorage({ guesses, solution: gameData.solution })
  }, [guesses, gameData, isInitialized])

  useEffect(() => {
    if (!isInitialized) return
    if (isGameWon) {
      const winMessage = WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      const delayMs = REVEAL_TIME_MS * WORD_LENGTH
      showSuccessAlert(winMessage, { delayMs })
    }
    if (isGameLost) {
      showErrorAlert(CORRECT_WORD_MESSAGE(gameData.solution), { persist: true })
    }
  }, [isGameWon, isGameLost, isInitialized, gameData.solution, showSuccessAlert, showErrorAlert])

  const clearCurrentRowClass = () => {
    setCurrentRowClass('')
  }

  const onChar = (value) => {
    if (
      unicodeLength(`${currentGuess}${value}`) <= WORD_LENGTH &&
      canPlay
    ) {
      setCurrentGuess(`${currentGuess}${value}`)
    }
  }

  const onDelete = () => {
    if (!canPlay) return
    setCurrentGuess(
      new GraphemeSplitter().splitGraphemes(currentGuess).slice(0, -1).join('')
    )
  }

  const onEnter = () => {
    if (!canPlay) {
      return
    }

    if (!(unicodeLength(currentGuess) === WORD_LENGTH)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(NOT_ENOUGH_LETTERS_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    if (!isWordInWordList(currentGuess)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    setIsRevealing(true)
    setTimeout(() => {
      setIsRevealing(false)
    }, REVEAL_TIME_MS * WORD_LENGTH)

    const winningWord = isWinningWord(currentGuess, gameData.solution)

    if (unicodeLength(currentGuess) === WORD_LENGTH && canPlay) {
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      if (winningWord) {
        setStats(addStatsForCompletedGame(stats, guesses.length))
        setIsGameWon(true)
        setTimeout(() => setIsStatsModalOpen(true), REVEAL_TIME_MS * WORD_LENGTH + 500)
        return
      }

      if (guesses.length === MAX_CHALLENGES - 1) {
        setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        setIsGameLost(true)
        setTimeout(() => setIsStatsModalOpen(true), REVEAL_TIME_MS * WORD_LENGTH + 500)
        return
      }
    }
  }

  const handlePlayAgain = () => {
    const newWord = getRandomWord()
    console.log('Nueva palabra:', newWord.solution, 'Longitud:', unicodeLength(newWord.solution))
    clearAlert()
    setGameData({ 
      solution: newWord.solution, 
      definition: newWord.definition,
      wordLength: unicodeLength(newWord.solution)
    })
    setGuesses([])
    setCurrentGuess('')
    setIsGameWon(false)
    setIsGameLost(false)
    setIsStatsModalOpen(false)
    saveGameStateToLocalStorage({ guesses: [], solution: newWord.solution })
  }

  if (!isInitialized || !gameData.solution) {
    return null
  }

  return (
    <div className="h-screen flex flex-col">
      <Navbar
        setIsInfoModalOpen={setIsInfoModalOpen}
        setIsStatsModalOpen={setIsStatsModalOpen}
        setIsSettingsModalOpen={() => {}}
      />

      <Instalador
        setIsInfoModalOpen={setIsInfoModalOpen}
        setIsStatsModalOpen={setIsStatsModalOpen}
      />

      <div className="pt-2 px-1 pb-8 md:max-w-7xl w-full mx-auto sm:px-6 lg:px-8 flex flex-col grow">
        <div className="pb-6">
          <Grid
            guesses={guesses}
            currentGuess={currentGuess}
            isRevealing={isRevealing}
            currentRowClassName={currentRowClass}
            solution={gameData.solution}
            wordLength={WORD_LENGTH}
          />
        </div>
        <Keyboard
          onChar={onChar}
          onDelete={onDelete}
          onEnter={onEnter}
          guesses={guesses}
          isRevealing={isRevealing}
          solution={gameData.solution}
        />

        <InfoModal
          isOpen={isInfoModalOpen}
          handleClose={() => setIsInfoModalOpen(false)}
        />
        <StatsModal
          isOpen={isStatsModalOpen}
          handleClose={() => setIsStatsModalOpen(false)}
          guesses={guesses}
          gameStats={stats}
          isGameLost={isGameLost}
          isGameWon={isGameWon}
          handleShareToClipboard={() => showSuccessAlert(GAME_COPIED_MESSAGE)}
          isHardMode={false}
          isDarkMode={false}
          isHighContrastMode={false}
          solution={gameData.solution}
          definition={gameData.definition}
          handlePlayAgain={handlePlayAgain}
        />
        <AlertContainer />
      </div>
        <Footer/>
    </div>
  )
}

export default App
